import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AgentAvatar from "../AgentAvatar";
import { AVATAR_SIZE, TEXT_INDENT } from "./conversationConstants";
import {
  FormSurface,
  StatusPill,
  useFormPalette,
} from "../forms/DynamicFormPrimitives";

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (v === null || v === undefined ? "" : String(v)))
    .filter((v) => v.length > 0);
}

const optionLetter = (index) => String.fromCharCode(65 + index); // 0 -> A, 1 -> B, ...

/**
 * Inline "ask_human" question card, modeled on Cursor's inline clarifying questions. Options are
 * stacked vertically and lettered A, B, C, … Clicking an option only selects it (single-select
 * replaces, multi-select toggles); nothing is sent until the user clicks Submit, so they can switch
 * choices first. Free text is an "Other" input. The PENDING and ANSWERED states share one layout —
 * answered simply locks interaction and reflects the chosen option(s) / free text.
 */
export default function AskHumanCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  chatSessionId,
  onSubmitAnswer = null,
  onOpenAgentSettings = null,
}) {
  const palette = useFormPalette();
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);

  const question =
    parsedArgs?.question || parsedResult?.question || "Quick question";
  const questionType =
    parsedArgs?.questionType === "multi" ? "multi" : "single";
  const suggestedAnswers = useMemo(
    () =>
      normalizeStringList(
        parsedArgs?.suggestedAnswers ?? parsedResult?.suggestedAnswers
      ),
    [parsedArgs, parsedResult]
  );
  const allowFreeText =
    parsedArgs?.allowFreeText === undefined
      ? true
      : Boolean(parsedArgs.allowFreeText);
  const questionId =
    parsedResult?.questionId || parsedArgs?.questionId || null;

  // The ask_human tool returns its PENDING envelope as the tool output, so the part is
  // "output-available" the instant the question is posted. Answered state is driven solely by the
  // result status flipping to ANSWERED (live resume or history reload via markAskHumanPartAnswered).
  const isAnswered = parsedResult?.status === "ANSWERED";

  const [selected, setSelected] = useState(() => new Set());
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const hasOptions = suggestedAnswers.length > 0;
  const canSubmit = typeof onSubmitAnswer === "function" && Boolean(questionId);

  // Which options were chosen, for the answered (read-only) view. Match by persisted index first,
  // then by label, so the full option list renders with the picked one(s) highlighted on reload.
  const answeredLabels = useMemo(
    () => normalizeStringList(parsedResult?.selectedOptions),
    [parsedResult]
  );
  const answeredState = useMemo(() => {
    const indexSet = new Set(
      Array.isArray(parsedResult?.selectedOptionIndices)
        ? parsedResult.selectedOptionIndices
            .map((n) => Number(n))
            .filter((n) => Number.isInteger(n))
        : []
    );
    const labelSet = new Set(answeredLabels);
    return { indexSet, labelSet };
  }, [parsedResult, answeredLabels]);

  const effectiveFreeText = isAnswered
    ? typeof parsedResult?.freeText === "string"
      ? parsedResult.freeText
      : ""
    : freeText;
  const trimmedFreeText = freeText.trim();

  const isOptionSelected = (label, index) => {
    if (isAnswered) {
      return (
        answeredState.indexSet.has(index) || answeredState.labelSet.has(label)
      );
    }
    return selected.has(index);
  };

  const interactive = !isAnswered && !submitting;

  const avatarClick =
    agentId && typeof onOpenAgentSettings === "function"
      ? (e) => {
          e.stopPropagation();
          onOpenAgentSettings(agentId);
        }
      : undefined;

  const doSubmit = async ({ selectedOptionIndices, freeTextValue }) => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const ok = await onSubmitAnswer(chatSessionId, {
        questionId,
        selectedOptionIndices,
        freeText: freeTextValue || null,
      });
      if (ok === false) {
        setSubmitError("Couldn't submit your answer. Please try again.");
        setSubmitting(false);
      }
      // On success, the resumed session streams a tool-output-available that flips this card to
      // its answered state, so we intentionally leave `submitting` on to keep controls locked.
    } catch (e) {
      setSubmitError("Couldn't submit your answer. Please try again.");
      setSubmitting(false);
    }
  };

  // Selecting an option only updates local state — the answer is not sent until the user clicks
  // Submit, so they can freely switch options first. Single-select replaces (and re-clicking the
  // chosen option clears it); multi-select toggles.
  const onOptionClick = (index) => {
    if (!interactive) return;
    setSelected((prev) => {
      if (questionType === "multi") {
        const next = new Set(prev);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        return next;
      }
      return prev.has(index) ? new Set() : new Set([index]);
    });
  };

  const handleSubmit = () => {
    const indices = Array.from(selected).sort((a, b) => a - b);
    doSubmit({
      selectedOptionIndices: indices,
      freeTextValue: trimmedFreeText || null,
    });
  };

  const canSubmitNow =
    selected.size > 0 || (allowFreeText && Boolean(trimmedFreeText));

  // Free text input shows while pending (if allowed); when answered, only if an answer was typed.
  const showFreeText = allowFreeText && (!isAnswered || Boolean(effectiveFreeText));

  return (
    <Box sx={{ px: 2.5, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar agentId={agentId} name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box sx={{ width: "75%", minWidth: "280px" }}>
          <FormSurface
            title="Quick question"
            subtitle={
              !isAnswered && questionType === "multi"
                ? "Select all that apply"
                : undefined
            }
            rightSlot={
              isAnswered ? (
                <StatusPill label="Answered" tone="success" />
              ) : undefined
            }
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: palette.textPrimary,
                mb: hasOptions || showFreeText ? 1.25 : 0,
              }}
            >
              {question}
            </Typography>

            {hasOptions && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {suggestedAnswers.map((label, i) => {
                  const sel = isOptionSelected(label, i);
                  return (
                    <Box
                      key={`opt-${i}`}
                      role="button"
                      tabIndex={interactive ? 0 : -1}
                      onClick={interactive ? () => onOptionClick(i) : undefined}
                      onKeyDown={
                        interactive
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onOptionClick(i);
                              }
                            }
                          : undefined
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.85,
                        borderRadius: "8px",
                        border: `1px solid ${
                          sel ? palette.selectedBorder : palette.chipBorder
                        }`,
                        backgroundColor: sel ? palette.selectedBg : "transparent",
                        opacity: isAnswered && !sel ? 0.5 : 1,
                        cursor: interactive ? "pointer" : "default",
                        transition: "border-color .12s, background-color .12s",
                        "&:hover": interactive
                          ? { borderColor: palette.selectedBorder }
                          : undefined,
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 20,
                          height: 20,
                          borderRadius: "5px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "11px",
                          fontWeight: 700,
                          // On select the badge fills with the accent (near-black in light, near-white
                          // in dark), so the letter must use the surface color to stay readable —
                          // using textPrimary here is the same tone as the fill (light-on-light).
                          color: sel ? palette.surface : palette.textSecondary,
                          backgroundColor: sel
                            ? palette.selectedBorder
                            : "transparent",
                          border: `1px solid ${
                            sel ? palette.selectedBorder : palette.chipBorder
                          }`,
                        }}
                      >
                        {optionLetter(i)}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: palette.textPrimary,
                          flex: 1,
                        }}
                      >
                        {label}
                      </Typography>
                      {sel && (
                        <CheckRoundedIcon
                          sx={{ fontSize: 16, color: palette.accent }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {showFreeText && (
              <Box sx={{ mt: hasOptions ? 1.25 : 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={5}
                  disabled={submitting || isAnswered}
                  value={effectiveFreeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  placeholder={hasOptions ? "Other…" : "Type your answer…"}
                  InputProps={{ sx: { fontSize: "13px" } }}
                />
              </Box>
            )}

            {!isAnswered && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.25,
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  disabled={submitting || !canSubmitNow}
                  onClick={handleSubmit}
                  startIcon={<SendOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: "none", fontSize: "12px" }}
                >
                  Submit
                </Button>
                {submitting && (
                  <CircularProgress size={14} sx={{ color: palette.accent }} />
                )}
              </Box>
            )}

            {submitError && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: palette.errorText,
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                {submitError}
              </Typography>
            )}
          </FormSurface>
        </Box>
      </Box>
    </Box>
  );
}
